import { ButtonLink } from "@ui/ButtonLink";
import askGVTLogo from "@ui/icons/AskGVTLogo.svg?raw";
import { HStack } from "@ui/Stack";
import { type JSX, Show } from "solid-js";
import { signInUrl } from "~/modules/shared/utils/auth";
import { pageVars } from "../pageVars";

type Props = {
	backLink?: string;
	variant?: "compact" | "marketing";
	/** Defaults to true. */
	showSignUp?: boolean;
	/** Rendered in the marketing header before "Are you a creator?". */
	actions?: JSX.Element;
	/**
	 * Marketing header only: drops Discover and merges the sign-in buttons,
	 * which share a destination, so the header fits a phone beside `actions`.
	 */
	condensed?: boolean;
};

/** Guest chrome for AskGVT consumer pages. */
export const SignedOutHeader = (props: Props) => {
	const vars = pageVars();
	const variant = () => props.variant ?? "compact";
	const showSignUp = () => props.showSignUp ?? true;

	return (
		<header
			class="gvtui-header askgvt-signed-out-header"
			classList={{
				"askgvt-signed-out-header--marketing": variant() === "marketing",
				"askgvt-signed-out-header--condensed":
					variant() === "marketing" && Boolean(props.condensed),
			}}
		>
			<Show
				when={variant() === "marketing"}
				fallback={
					<HStack fullWidth justify="space-between" align="center">
						<HStack align="center" spacing="small">
							<Show when={props.backLink}>
								{(href) => (
									<ButtonLink
										aria-label="Back"
										variant="tertiary"
										symbol="arrow_back"
										class="askgvt-signed-out-header__back"
										href={href()}
									/>
								)}
							</Show>
							<Show when={vars.getTheAppUrl}>
								{(href) => (
									<ButtonLink
										href={href()}
										variant="tertiary"
										symbol="phone_iphone"
									>
										Get the app
									</ButtonLink>
								)}
							</Show>
						</HStack>
						<HStack align="center" spacing="small">
							<ButtonLink href={signInUrl()} variant="text" theme="monochrome">
								Log in
							</ButtonLink>
							<Show when={showSignUp()}>
								<ButtonLink href={signInUrl()} variant="primary">
									Sign up
								</ButtonLink>
							</Show>
						</HStack>
					</HStack>
				}
			>
				<HStack fullWidth justify="space-between" align="center">
					<HStack align="center" spacing="large">
						<Show when={props.backLink}>
							{(href) => (
								<ButtonLink
									aria-label="Back"
									variant="tertiary"
									symbol="arrow_back"
									class="askgvt-signed-out-header__back"
									href={href()}
								/>
							)}
						</Show>
						<a
							href="/"
							class="askgvt-signed-out-header__logo"
							aria-label="AskGVT home"
						>
							<span class="brand-logo" innerHTML={askGVTLogo} />
						</a>
						<Show when={!props.condensed}>
							<ButtonLink href="/discovery" variant="tertiary" symbol="explore">
								Discover
							</ButtonLink>
						</Show>
						<Show when={vars.getTheAppUrl}>
							{(href) => (
								<ButtonLink
									href={href()}
									variant="tertiary"
									symbol="phone_iphone"
								>
									Get the app
								</ButtonLink>
							)}
						</Show>
					</HStack>
					<HStack
						align="center"
						spacing={props.condensed ? "medium" : "large"}
						class="askgvt-signed-out-header__end"
					>
						{props.actions}
						<ButtonLink
							href="/studio"
							variant="text"
							symbol="account_circle"
							class="askgvt-signed-out-header__creator"
						>
							Are you a creator?
						</ButtonLink>
						<HStack align="center" spacing="medium">
							<span
								class="askgvt-signed-out-header__divider"
								aria-hidden="true"
							/>
							<Show
								when={!props.condensed}
								fallback={
									<ButtonLink href={signInUrl()} variant="tertiary">
										{showSignUp() ? "Log in / Sign up" : "Log in"}
									</ButtonLink>
								}
							>
								<ButtonLink
									href={signInUrl()}
									variant="text"
									theme="monochrome"
								>
									Log in
								</ButtonLink>
								<Show when={showSignUp()}>
									<ButtonLink href={signInUrl()} variant="primary">
										Sign up
									</ButtonLink>
								</Show>
							</Show>
						</HStack>
					</HStack>
				</HStack>
			</Show>
		</header>
	);
};
